// Secure HeatMap Data Worker - Native Worker API implementation
// Converts the original workerized implementation to use secure blob-based approach

// Secure worker code - statically defined, includes all the heatmap processing logic
const HEATMAP_WORKER_CODE = `
// Worker-side date-fns parse function (inline implementation to avoid imports)
function parseDate(dateString, formatString, referenceDate) {
  // Simple date parser for "yyyy-MM-dd HH:mm:ssX" format
  // This is a simplified version for the worker context
  try {
    return new Date(dateString).getTime();
  } catch (error) {
    console.error('Date parsing error:', error);
    return new Date().getTime();
  }
}

const THRESHOLD = 2000;

// Merge downtime events function
const mergeDowntimeEvents = (input, translations) => {
  const aggregatedData = input.reduce((acc, item) => {
    const existingItem = acc.find(entry => {
      return (
        entry.circuit_name === item.circuit_name &&
        entry.local_exit_time === item.local_entry_time
      );
    });
    if (existingItem) {
      existingItem.downtimeInMin += item.downtimeInMin;
      existingItem.message = (translations['circuit.unavailableTimeRange'] || 'Unavailable: {{startTime}} - {{endTime}}')
        .replace('{{startTime}}', existingItem.local_entry_time)
        .replace('{{endTime}}', item.local_exit_time);
    } else {
      acc.push(Object.assign({}, item));
    }
    return acc;
  }, []);
  return aggregatedData;
};

// Convert UTC date to timestamp
const getTimestampFromUTCString = (date, format) => {
  format = format || "yyyy-MM-dd HH:mm:ssX";
  return parseDate(date + "Z", format, new Date());
};

// Main heatmap data processing function
function processHeatMapData(
  trendCircuitsApiData,
  selectedTrendCircuits,
  circuitsData,
  minProcessedTime,
  timeFilter,
  translations = {}
) {
  let timeStampsJson = [];
  let apiData = trendCircuitsApiData && trendCircuitsApiData.data;
  let circuitsRefs = {};

  if (apiData && apiData.length > 0) {
    apiData.filter(item => {
      const downtimeInMin = item.downtime / 60000;
      const local_parse_time = getTimestampFromUTCString(item.entry_ts);
      const local_entry_time = new Date(local_parse_time);
      const exit_time = new Date(local_parse_time + item.downtime);
      const message = (translations['circuit.unavailableTimeRange'] || 'Unavailable: {{startTime}} - {{endTime}}')
        .replace('{{startTime}}', local_entry_time.toLocaleTimeString())
        .replace('{{endTime}}', exit_time.toLocaleTimeString());

      // Custom timeframe with downtime to entry_ts is beyond timePeriod in the request payload,
      // but entry_ts is still within request payload boundaries.
      if (local_entry_time.getTime() <= new Date(timeFilter)) {
        let entry = {};
        entry.circuit_name = item.circuit_display_name;
        entry.downtimeInMin = downtimeInMin;
        entry.local_entry_time = local_entry_time;
        entry.local_exit_time = exit_time;
        entry.message = message;
        timeStampsJson.push(entry);
      }
    });

    timeStampsJson = timeStampsJson.length > THRESHOLD
      ? mergeDowntimeEvents(timeStampsJson, translations)
      : timeStampsJson;
  } else if (apiData && apiData.length === 0) {
    timeStampsJson = [];
  }

  if (Array.isArray(selectedTrendCircuits)) {
    // DataPoints for missing downtime circuits,
    // Even if there is downtime we should see selected circuits complete uptime
    selectedTrendCircuits.forEach(element => {
      let startEntry = {}, endEntry = {};
      startEntry.circuit_name = element;
      startEntry.local_entry_time = new Date(minProcessedTime);
      timeStampsJson.push(startEntry);
      endEntry.circuit_name = element;
      endEntry.local_entry_time = new Date(timeFilter);
      timeStampsJson.push(endEntry);
    });

    if (circuitsData) {
      circuitsData.forEach(item => {
        if (selectedTrendCircuits.includes(item.circuit_name)) {
          circuitsRefs[item.circuit_name] = item;
        }
      });
    }
  }

  return [timeStampsJson, circuitsRefs];
}

// Worker message handler
self.onmessage = function(event) {
  const { data } = event;

  try {
    if (data.type === 'processHeatMapData') {
      const result = processHeatMapData(
        data.trendCircuitsApiData,
        data.selectedTrendCircuits,
        data.circuitsData,
        data.minProcessedTime,
        data.timeFilter,
        data.i18nText
      );

      self.postMessage({
        type: 'heatmap_result',
        success: true,
        data: result,
        timestamp: new Date().toISOString()
      });
    } else {
      self.postMessage({
        type: 'error',
        success: false,
        message: 'Unknown message type: ' + data.type
      });
    }
  } catch (error) {
    self.postMessage({
      type: 'error',
      success: false,
      message: 'Error processing heatmap data: ' + error.message,
      error: error.toString()
    });
  }
};

// Handle worker errors
self.onerror = function(error) {
  console.error('Heatmap worker error:', error);
  self.postMessage({
    type: 'error',
    success: false,
    message: 'Worker error occurred',
    error: error.message
  });
};

// Send ready message
self.postMessage({
  type: 'ready',
  message: 'Heatmap Worker is ready!',
  timestamp: new Date().toISOString()
});
`;

// Validate worker code integrity
function validateWorkerCode(code) {
  const dangerousPatterns = [
    /eval\s*\(/,
    /Function\s*\(/,
    /setTimeout\s*\(/,
    /setInterval\s*\(/,
    /document\./,
    /window\./,
    /location\./,
    /XMLHttpRequest/,
    /fetch\s*\(/
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(code)) {
      throw new Error(
        `Security violation: Dangerous pattern detected in worker code`
      );
    }
  }

  return true;
}

/**
 * Adds processHeatMapData helper method to worker instance
 * @param {Worker} worker - Worker instance
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @returns {Worker} Worker with helper method attached
 */
function addProcessHeatMapDataMethod(worker, timeout = 30000) {
  worker.processHeatMapData = function (
    trendCircuitsApiData,
    selectedTrendCircuits,
    circuitsData,
    minProcessedTime,
    timeFilter,
    i18nText
  ) {
    return new Promise((resolve, reject) => {
      let timeoutId;
      let isResolved = false;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        worker.removeEventListener("message", messageHandler);
        worker.removeEventListener("error", errorHandler);
      };

      const messageHandler = function (event) {
        if (isResolved) return;

        try {
          if (event.data.type === "heatmap_result") {
            isResolved = true;
            cleanup();
            if (event.data.success) {
              resolve(event.data.data);
            } else {
              reject(
                new Error(
                  event.data.message || "Unknown heatmap processing error"
                )
              );
            }
          } else if (event.data.type === "error") {
            isResolved = true;
            cleanup();
            reject(new Error(event.data.message || "Worker error occurred"));
          }
        } catch (error) {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            reject(
              new Error("Error handling worker response: " + error.message)
            );
          }
        }
      };

      const errorHandler = function (error) {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          reject(new Error("Worker error: " + error.message));
        }
      };

      try {
        worker.addEventListener("message", messageHandler);
        worker.addEventListener("error", errorHandler);

        worker.postMessage({
          type: "processHeatMapData",
          trendCircuitsApiData,
          selectedTrendCircuits,
          circuitsData,
          minProcessedTime,
          timeFilter,
          i18nText
        });

        timeoutId = setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            reject(
              new Error(`Heatmap processing timeout after ${timeout / 1000} seconds`)
            );
          }
        }, timeout);
      } catch (error) {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          reject(
            new Error(
              "Failed to setup worker communication: " + error.message
            )
          );
        }
      }
    });
  };

  return worker;
}

/**
 * Enhances worker with cleanup and security features
 * @param {Worker} worker - Worker instance
 * @param {string} workerURL - Worker URL to revoke (optional for blob-based workers)
 * @returns {Worker} Enhanced worker instance
 */
function enhanceWorkerSecurity(worker, workerURL = null) {
  const originalTerminate = worker.terminate.bind(worker);
  let isTerminated = false;

  worker.terminate = function () {
    if (!isTerminated) {
      isTerminated = true;
      if (workerURL) {
        URL.revokeObjectURL(workerURL);
      }
      originalTerminate();
    }
  };

  // Auto-cleanup on error
  worker.onerror = function (error) {
    console.error("Heatmap worker error detected, cleaning up:", error);
    if (!isTerminated) {
      worker.terminate();
    }
  };

  // Timeout cleanup (prevent zombie workers)
  setTimeout(() => {
    if (!isTerminated) {
      console.warn("Heatmap worker auto-cleanup after 10 minutes");
      worker.terminate();
    }
  }, 10 * 60 * 1000);

  return worker;
}

/**
 * Creates secure heatmap worker using Blob approach
 * @returns {Worker} Worker instance with helper methods
 */
export function createHeatmapWorker() {
  try {
    validateWorkerCode(HEATMAP_WORKER_CODE);

    const blob = new Blob([HEATMAP_WORKER_CODE], {
      type: "application/javascript;charset=utf-8"
    });

    const workerURL = URL.createObjectURL(blob);

    let worker;
    try {
      worker = new Worker(workerURL);
    } catch (error) {
      console.error("Failed to create worker instance:", error);
      URL.revokeObjectURL(workerURL);
      throw error;
    }

    enhanceWorkerSecurity(worker, workerURL);
    addProcessHeatMapDataMethod(worker);

    return worker;
  } catch (error) {
    console.error("Failed to create secure heatmap worker:", error);
    throw error;
  }
}
